/**
 * Локализация системных инструментов (`ToolSummary.type === 'System'`).
 *
 * Бэк отдаёт title/summary/description на английском (или на языке контента,
 * без учёта UI-локали). Здесь маппим известные tool.code → локализованные
 * строки через $localize. Если инструмента нет в маппинге — фолбэк на
 * оригинальные поля из ToolSummary, чтобы новые тулы бэка не пропадали
 * из UI пока фронт не догонит.
 *
 * Использовать `systemToolTitle(tool)` / `systemToolSummary(tool)` вместо
 * прямых `tool.title|summary` везде где отображаются системные
 * инструменты (agent-edit tools list, system-tool page, и т.д.).
 *
 * `description` НЕ локализуется: этот текст — промпт для модели, а не
 * подпись для человека. См. `systemToolDescription` ниже.
 *
 * Source-строки английские, переводы в public/i18n/ru.json по ключам
 * `systemToolCatalog.<tool-code>.<title|summary>`.
 */

import { ToolSummary } from '@generated/models';

interface LocalizedTool {
  title: string;
  summary: string;
}

/**
 * ФУНКЦИЯ, а не const-объект: $localize строки не эвалятся на верхнем
 * уровне модуля (тесно связаны с рантаймом loadTranslations и корнем
 * бандла). Возвращаем свежий объект при каждом вызове — не хот-path.
 */
function catalog(): Record<string, LocalizedTool> {
  return {
    'end-call': {
      title: $localize`:@@systemToolCatalog.end-call.title:End call`,
      summary: $localize`:@@systemToolCatalog.end-call.summary:Lets the agent end the conversation when the goal is met or the user asks to hang up.`,
    },
    'transfer-to-agent': {
      title: $localize`:@@systemToolCatalog.transfer-to-agent.title:Transfer to another agent`,
      summary: $localize`:@@systemToolCatalog.transfer-to-agent.summary:Hand the conversation off to another AI agent based on a rule.`,
    },
    'transfer-to-operator': {
      title: $localize`:@@systemToolCatalog.transfer-to-operator.title:Transfer to a human operator`,
      summary: $localize`:@@systemToolCatalog.transfer-to-operator.summary:Transfer the call to a live operator over SIP to a given phone number.`,
    },
    'skip-turn': {
      title: $localize`:@@systemToolCatalog.skip-turn.title:Skip turn`,
      summary: $localize`:@@systemToolCatalog.skip-turn.summary:The agent intentionally stays silent and waits for the user to continue.`,
    },
    'request-info': {
      title: $localize`:@@systemToolCatalog.request-info.title:Ask for clarification`,
      summary: $localize`:@@systemToolCatalog.request-info.summary:The agent asks a clarifying question if information is missing.`,
    },
    'get-knowledge': {
      title: $localize`:@@systemToolCatalog.get-knowledge.title:Ask`,
      summary: $localize`:@@systemToolCatalog.get-knowledge.summary:The agent answers from knowledge you type in, instead of making things up.`,
    },
    'detect-language': {
      title: $localize`:@@systemToolCatalog.detect-language.title:Detect language`,
      summary: $localize`:@@systemToolCatalog.detect-language.summary:Detects the caller's language and switches the agent to it.`,
    },
  };
}

export function systemToolTitle(tool: ToolSummary | null | undefined): string {
  if (!tool?.code) return tool?.title ?? '';
  return catalog()[tool.code]?.title ?? tool.title ?? tool.code;
}

export function systemToolSummary(tool: ToolSummary | null | undefined): string {
  if (!tool?.code) return tool?.summary ?? '';
  return catalog()[tool.code]?.summary ?? tool.summary ?? '';
}

/**
 * Дефолтное описание инструмента — ТОЛЬКО с бэка, без подмены переводом.
 *
 * Этот текст уезжает модели: бэк в диспатче берёт
 * `at.CustomDescription ?? at.Tool.Description`, то есть всё, что здесь
 * вернётся и попадёт в `custom_description`, станет инструкцией для LLM.
 * Раньше тут возвращался свой локализованный текст, и «Show default»
 * записывал в агента формулировку «…цель звонка достигнута», от которой
 * звонок завершался без повода (SKN-360). Переводим только подписи для
 * человека — title и summary.
 */
export function systemToolDescription(tool: ToolSummary | null | undefined): string {
  return tool?.description ?? '';
}
